/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {ColDef, ColumnState} from 'ag-grid-community';
import {GenericObject} from '@ion/core/src/types/entities';
import {useIsPhone} from '../hooks/useMedia';
import {
  stateToDefinitions
} from './ColumnResolver';
import {
  ColumnDefinitions
} from './useColumnDefinitions';

export interface useColumnStateProps {
  columnState: ColumnState[] | null;
  columnDefinitions: ColumnDefinitions;
  resolver?: Function;
  resolvePersistedColumnState?: Function;
  columnResolver?: Function;
  showTripVisible?: boolean
  isBoundaryFilterEnabled?: boolean;
  resolverOptions?: GenericObject;
  effectDependencies?: any;
};

/**
 * @useColumnState
 *
 * Provides a Hook which allows for managing the display state and current column selections
 * state of an IonGrid. The state returned by this hook is to be passed to an IonGrid instance,
 * and optionally used for persisting the current column selections (either to the application
 * store, externally to CTC, or both).
 *
 * ```typescript
 *
 *     const definitions = useColumnState({
 *       columnDefinitions : getColumnDefinitions(),
 *       columnState
 *     });
 *
 *     const {
 *       showColumnChooser,
 *       columnDefinitions,
 *       onColumnChooserSave,
 *       toggleColumnChooser
 *     } = useColumnChooser(definitions);
 *
 *     ...
 *
 *    <IonGrid ...
 *      showColumnChooser={showColumnChooser}
 *      onColumnChooserSave={onColumnChooserSave}
 *      onColumnChooserClose={function() {
 *        toggleColumnChooser(false);
 *      }}
 *      ... />
 *
 * ```
 */
export const useColumnState = ({
  columnState,
  columnDefinitions,
  resolver,
  isBoundaryFilterEnabled,
  showTripVisible,
  resolvePersistedColumnState,
  resolverOptions,
  columnResolver,
  effectDependencies
}: useColumnStateProps) => {
  const isPhone = useIsPhone();
  const columns = columnDefinitions[!isPhone ? 'desktop' : 'mobile'];
  const resolvedColumnState = resolvePersistedColumnState ? resolvePersistedColumnState(columnState) : columnState;
  const currentColumns = columnResolver ? columnResolver(resolvedColumnState, resolverOptions) : resolvedColumnState;
  const [definitions, setDefinitions] = useState<ColDef[]>(
    stateToDefinitions(currentColumns, columns, resolver)
  );

  useEffect(() => {
    setDefinitions(stateToDefinitions(currentColumns, columns, resolver));
  }, [columnState, isBoundaryFilterEnabled, showTripVisible, resolverOptions, effectDependencies]);

  return {definitions, defaultDefinitions: columns};
};

export default useColumnState;
